export const EXTERNAL_ECHO_SOURCES = {
  NATIVE_APP: 'native_app',
  META_BUSINESS_AGENT: 'meta_business_agent',
};

const META_BUSINESS_AGENT_SOURCE_ALIASES = new Set([
  EXTERNAL_ECHO_SOURCES.META_BUSINESS_AGENT,
  'meta_business_agent_standby',
  'meta_business_agent_recovered',
  'business_agent',
  'bizagent',
]);

const NATIVE_APP_SOURCE_ALIASES = new Set([
  EXTERNAL_ECHO_SOURCES.NATIVE_APP,
  'native',
  'whatsapp_native_app',
]);

const readAttribute = (attributes, camelCaseKey, snakeCaseKey) =>
  attributes?.[camelCaseKey] ?? attributes?.[snakeCaseKey];

const normalizeSource = source =>
  String(source || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

/**
 * Resolves who produced an external WhatsApp echo.
 *
 * New bidirectional transcript rows carry the reserved
 * `metaBusinessAgentMessage` flag. External echoes keep using
 * `externalEchoSource`; legacy standby/recovery flags only identify Meta when
 * paired with an external echo so inbound customer messages are never labelled
 * as AI.
 */
export const getExternalEchoSource = (contentAttributes = {}) => {
  const isTrustedMetaBusinessAgentMessage =
    readAttribute(
      contentAttributes,
      'metaBusinessAgentMessage',
      'meta_business_agent_message'
    ) === true;
  if (isTrustedMetaBusinessAgentMessage) {
    return EXTERNAL_ECHO_SOURCES.META_BUSINESS_AGENT;
  }

  const isExternalEcho =
    readAttribute(contentAttributes, 'externalEcho', 'external_echo') === true;
  if (!isExternalEcho) return null;

  const explicitSource = readAttribute(
    contentAttributes,
    'externalEchoSource',
    'external_echo_source'
  );
  const messageOrigin = readAttribute(
    contentAttributes,
    'messageOrigin',
    'message_origin'
  );
  const normalizedSource = normalizeSource(explicitSource || messageOrigin);

  if (META_BUSINESS_AGENT_SOURCE_ALIASES.has(normalizedSource)) {
    return EXTERNAL_ECHO_SOURCES.META_BUSINESS_AGENT;
  }
  if (NATIVE_APP_SOURCE_ALIASES.has(normalizedSource)) {
    return EXTERNAL_ECHO_SOURCES.NATIVE_APP;
  }
  if (normalizedSource) return normalizedSource;

  const isMetaBusinessAgentLegacyEcho =
    readAttribute(
      contentAttributes,
      'metaBusinessAgentStandby',
      'meta_business_agent_standby'
    ) === true ||
    readAttribute(
      contentAttributes,
      'metaBusinessAgentRecovered',
      'meta_business_agent_recovered'
    ) === true;

  return isMetaBusinessAgentLegacyEcho
    ? EXTERNAL_ECHO_SOURCES.META_BUSINESS_AGENT
    : EXTERNAL_ECHO_SOURCES.NATIVE_APP;
};

export const isMetaBusinessAgentMessage = contentAttributes =>
  getExternalEchoSource(contentAttributes) ===
  EXTERNAL_ECHO_SOURCES.META_BUSINESS_AGENT;
